import React from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { FixedSizeGrid as Grid } from 'react-window';

import { Flex, Stack } from '@mantine/core';

import { ClonotypePlot } from './ClonotypePlot';
import { useNetworkMapClonotypesContext } from './NetworkMapClonotypesContext';
import { NetworkMapClonotype } from './networkMap.utils';
import { CellResponseWithClonotype } from '../generated/generatedOpenAPI';
import { useDebouncedResizeObserver } from '../hooks/useDebouncedResizeObserver';
import { selectHideSingletons } from '../store/slices/antibodyAnalysis.slice';
import { defaultGridSize, defaultSorting } from '../store/slices/clonotypes.slice';
import { AppState } from '../store/store';

const MAX_PLOT_HEIGHT = 350;

interface NetworkMapGridProps {
  columnCount: number;
  rowCount: number;
  clonotypeCellWidth: number;
  clonotypeCellHeight: number;
  width: number;
  height: number;
  clonotypes: NetworkMapClonotype[];
}

function NetworkMapGridUnmemoized({ columnCount, rowCount, clonotypeCellWidth, clonotypeCellHeight, width, height, clonotypes }: NetworkMapGridProps) {
  const { id: experimentId } = useParams<{ id: string }>();
  const sorting = useSelector((state: AppState) => state.clonotypes[experimentId as string]?.sorting ?? defaultSorting);

  const sortedClonotypes = React.useMemo(() => {
    // Helper function to extract sorting values
    const getSortingValue = (clonotype: NetworkMapClonotype, sortId: keyof CellResponseWithClonotype) => {
      const cell = clonotype.nodes?.[0]?.cells?.[0];
      if (!cell) {
        return null;
      }

      if (sortId === 'irVdj1VCall') {
        return `${cell.irVdj1VCall}/${cell.irVdj1JCall}`;
      }
      if (sortId === 'irVj1VCall') {
        return `${cell.irVj1VCall}/${cell.irVj1JCall}`;
      }
      return cell[sortId];
    };

    // Copy before sorting: `clonotypes` is shared via NetworkMapClonotypesContext, so sorting it
    // in place would also reorder the same array that ControlBar and the AB table read.
    return [...clonotypes].sort((a, b) => {
      const sortId = sorting.id as keyof CellResponseWithClonotype;

      // Extract clonotype IDs for fallback sorting
      const clonotypeIdA = Number(a.clonotypeId);
      const clonotypeIdB = Number(b.clonotypeId);

      // Handle specific sorting cases
      const valueA = sortId === 'clonotypeSize' ? a.clonotypeSize : sortId === 'irVdj1CCall' ? a.irVdj1CCall : getSortingValue(a, sortId);
      const valueB = sortId === 'clonotypeSize' ? b.clonotypeSize : sortId === 'irVdj1CCall' ? b.irVdj1CCall : getSortingValue(b, sortId);

      // Fallback sorting by clonotype ID if cells are missing
      if (valueA === null && valueB === null) {
        return clonotypeIdA - clonotypeIdB;
      }
      if (valueA === null) {
        return 1;
      }
      if (valueB === null) {
        return -1;
      }

      // Handle numeric sorting
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueA === valueB ? clonotypeIdA - clonotypeIdB : sorting.desc ? valueB - valueA : valueA - valueB;
      }

      // Handle string sorting
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const comparison = sorting.desc ? valueB.localeCompare(valueA) : valueA.localeCompare(valueB);
        return comparison === 0 ? clonotypeIdA - clonotypeIdB : comparison;
      }

      // Default case
      return 0;
    });
  }, [clonotypes, sorting.desc, sorting.id]);

  const renderItem = React.useCallback(
    ({ columnIndex, rowIndex, style, data }: any) => {
      const index = rowIndex * columnCount + columnIndex;
      const clonotype = data[index];
      if (!clonotype) {
        return null;
      }
      return (
        <Flex
          data-testid="clonotype-plot-flex"
          key={clonotype.clonotypeId}
          justify="center"
          align="center"
          style={style}
          w={clonotypeCellWidth}
          h={clonotypeCellHeight}
        >
          <ClonotypePlot key={clonotype.clonotypeId} clonotype={clonotype} width={clonotypeCellWidth} height={clonotypeCellHeight} />
        </Flex>
      );
    },
    [columnCount, clonotypeCellWidth, clonotypeCellHeight],
  );

  return (
    <div data-testid="network-map-grid">
      <Grid
        columnCount={columnCount}
        rowCount={rowCount}
        columnWidth={clonotypeCellWidth}
        rowHeight={clonotypeCellHeight}
        width={width}
        height={height}
        itemKey={({ columnIndex, rowIndex }) => `${rowIndex}-${columnIndex}`}
        itemData={sortedClonotypes}
        overscanRowCount={2}
        className="custom-scrollbar"
      >
        {renderItem}
      </Grid>
    </div>
  );
}

const NetworkMapGrid = React.memo(NetworkMapGridUnmemoized);

export function NetworkMapVisualization() {
  const { ref: listContainerRef, height, width } = useDebouncedResizeObserver<HTMLDivElement>(200); // 200ms debounce delay

  const { id: experimentId } = useParams<{ id: string }>();

  const { clonotypes: allClonotypes } = useNetworkMapClonotypesContext();
  const hideSingletons = useSelector(selectHideSingletons);

  // Singleton = a clonotype whose total cell count across all its nodes is 1. When the
  // "Hide Singletons" toggle in ControlBar is on, hide them from the network map too so
  // the map stays in sync with the AB table (both should show the same universe).
  const clonotypes = React.useMemo(() => {
    if (!hideSingletons) {
      return allClonotypes;
    }
    return allClonotypes.filter((c) => c.nodes.reduce((sum, n) => sum + n.cells.length, 0) > 1);
  }, [allClonotypes, hideSingletons]);

  const gridSize = useSelector((state: AppState) => state.clonotypes[experimentId!]?.gridSize ?? defaultGridSize);

  const clonotypeCellWidth = React.useMemo(() => (width - 20) / gridSize, [width, gridSize]);
  const clonotypeCellHeight = React.useMemo(() => (clonotypeCellWidth > MAX_PLOT_HEIGHT ? MAX_PLOT_HEIGHT : clonotypeCellWidth), [clonotypeCellWidth]);

  const rowCount = React.useMemo(() => Math.ceil(clonotypes.length / gridSize), [clonotypes.length, gridSize]);
  const gridDimensions = React.useMemo(() => ({ width, height: Math.max(0, height) }), [width, height]);

  return (
    <Stack ref={listContainerRef} gap={0} h="100%">
      <Stack gap={0} mr="sm">
        <NetworkMapGrid
          columnCount={gridSize}
          rowCount={rowCount}
          clonotypeCellWidth={clonotypeCellWidth}
          clonotypeCellHeight={clonotypeCellHeight}
          width={gridDimensions.width}
          height={gridDimensions.height}
          clonotypes={clonotypes}
        />
      </Stack>
    </Stack>
  );
}
